/**
 * StoreItemDetail - Single product: image, price, qty, checkout (shared by Store route and StoreItemOverlay).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { createCheckoutSession } from '../../helpers/storeHelpers';
import { getStoreItemDisplayImage } from '../../helpers/storeItemImages';
import { searchUsers } from '../../helpers/searchHelpers';
import { selectUser } from '../../store/auth.reducer';
import useSwipeToClose from '../../hooks/useSwipeToClose';
import toast from 'react-hot-toast';
import ShippingAddressSection from './ShippingAddressSection';
import UserAvatar from '../Profile/UserAvatar';

const USER_SEARCH_DEBOUNCE_MS = 300;

function StoreItemDetail({ item, onBack }) {
  const currentUser = useSelector(selectUser);
  const [quantity, setQuantity] = useState(1);
  const [ordering, setOrdering] = useState(false);
  const [selectedShippingAddress, setSelectedShippingAddress] = useState(null);

  const [recipientUser, setRecipientUser] = useState(null);
  const [message, setMessage] = useState('');
  const [doodle, setDoodle] = useState('');
  const [userQuery, setUserQuery] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [userSearchBusy, setUserSearchBusy] = useState(false);

  const isPostcard = item?.item_type === 'postcard';
  const needsShipping = item?.item_type === 'physical' || item?.item_type === 'postcard';

  const runUserSearch = useCallback(async (q) => {
    if (!q?.trim()) {
      setUserResults([]);
      return;
    }
    setUserSearchBusy(true);
    try {
      const users = await searchUsers(q.trim());
      setUserResults(Array.isArray(users) ? users : []);
    } catch {
      setUserResults([]);
    } finally {
      setUserSearchBusy(false);
    }
  }, []);

  useEffect(() => {
    if (!isPostcard) return;
    const t = setTimeout(() => runUserSearch(userQuery), USER_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [userQuery, isPostcard, runUserSearch]);

  useEffect(() => {
    setSelectedShippingAddress(null);
  }, [item?.item_id]);

  const handleCheckout = async () => {
    if (!item?.is_available || ordering) return;

    if (!selectedShippingAddress?.address_id) {
      toast.error('Choose a shipping address');
      return;
    }

    if (item.item_type === 'postcard') {
      if (!recipientUser) {
        toast.error('Choose who will receive the postcard');
        return;
      }
      if (!message.trim()) {
        toast.error('Add a message for the postcard');
        return;
      }
    }

    setOrdering(true);
    try {
      let result;
      if (item.item_type === 'physical') {
        result = await createCheckoutSession(item.item_id, quantity, selectedShippingAddress.address_id);
      } else if (item.item_type === 'postcard') {
        result = await createCheckoutSession(item.item_id, quantity, selectedShippingAddress.address_id, {
          recipient_user_id: recipientUser.user_id,
          message: message.trim(),
          doodle: doodle.trim(),
        });
      } else {
        toast.error('Checkout unavailable');
        return;
      }
      const { url } = result;
      if (url) window.location.href = url;
      else toast.error('Checkout unavailable');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setOrdering(false);
    }
  };

  const imgSrc = item ? getStoreItemDisplayImage(item) : null;

  // Swipe-back is only wired when this component is rendered inline by a route
  // (e.g. desktop `/store/:id`). Inside `StoreItemOverlay`, the surrounding
  // `SlideOverlay` already owns the gesture, so `onBack` is omitted there.
  const swipeBackEnabled = typeof onBack === 'function';
  const { ref: panelRef, dragX } = useSwipeToClose({
    open: true,
    direction: 'right',
    onClose: onBack,
    enabled: swipeBackEnabled,
  });
  const dragging = dragX !== 0;
  const panelStyle = dragging ? { transform: `translateX(${dragX}px)` } : undefined;
  const transitionClass = dragging ? 'transition-none' : 'transition-transform duration-300 ease-zen';

  return (
    <div
      ref={panelRef}
      style={panelStyle}
      className={`flex min-h-0 flex-1 flex-col overflow-hidden bg-charcoal ${transitionClass}`}
      data-testid="store-item-detail"
    >
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain px-4 pb-4 pt-0">
        {typeof onBack === 'function' && (
          <button type="button" onClick={onBack} className="text-ether text-sm hover:underline mb-2 pt-4">
            ← Back
          </button>
        )}
        {imgSrc ? (
          <img src={imgSrc} alt={item.name} className="w-full max-w-xs mx-auto rounded-2xl object-cover" />
        ) : (
          <div className="w-full max-w-xs mx-auto aspect-square rounded-2xl bg-shadow flex items-center justify-center text-6xl font-bold text-ether/50">
            {item.name.charAt(0).toUpperCase()}
          </div>
        )}
        <h1 className="font-display text-xl font-bold text-ash mt-4">{item.name}</h1>
        <p className="text-ether font-bold text-lg">${parseFloat(item.price).toFixed(2)}</p>
        {item.description && (
          <p className="text-silver text-sm mt-2">{item.description}</p>
        )}

        {isPostcard && (
          <div className="mt-4 space-y-3 border-t border-shadow pt-4">
            <div>
              <span className="block text-ash text-sm font-medium mb-1">Send to</span>
              {recipientUser ? (
                <div className="flex items-center gap-3 rounded-xl border border-shadow bg-void/50 px-3 py-2.5">
                  <UserAvatar
                    user={recipientUser}
                    className="h-11 w-11 flex-shrink-0 rounded-full"
                    emojiClassName="text-lg"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-ash">
                      {recipientUser.displayname?.trim()
                        ? recipientUser.displayname
                        : `@${recipientUser.username}`}
                    </p>
                    {recipientUser.displayname?.trim() ? (
                      <p className="truncate text-xs text-silver">@{recipientUser.username}</p>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setRecipientUser(null);
                      setUserQuery('');
                      setUserResults([]);
                    }}
                    className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl text-xl leading-none text-silver transition-colors hover:bg-shadow/80 hover:text-ash"
                    aria-label="Remove recipient and search again"
                    data-testid="postcard-recipient-clear"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <>
                  <label htmlFor="postcard-recipient-search" className="sr-only">
                    Search recipient by name or username
                  </label>
                  <input
                    id="postcard-recipient-search"
                    type="text"
                    value={userQuery}
                    onChange={(e) => setUserQuery(e.target.value)}
                    placeholder="Search by name or @username…"
                    className="w-full px-3 py-2 rounded-xl bg-shadow border border-charcoal text-ash placeholder-silver text-sm focus:outline-none focus:ring-2 focus:ring-ether/50"
                    data-testid="postcard-recipient-search"
                  />
                  <div className="max-h-40 overflow-y-auto mt-2 rounded-xl border border-shadow bg-void/50">
                    {userSearchBusy ? (
                      <p className="text-silver text-xs p-2">Searching…</p>
                    ) : userResults.length === 0 ? (
                      <p className="text-silver text-xs p-2">
                        {userQuery.trim() ? 'No users found' : 'Type to search'}
                      </p>
                    ) : (
                      <ul className="py-1">
                        {userResults.map((u) => {
                          const isSelf = currentUser && u.user_id === currentUser.user_id;
                          return (
                            <li key={u.user_id}>
                              <button
                                type="button"
                                disabled={isSelf}
                                onClick={() => {
                                  if (isSelf) return;
                                  setRecipientUser(u);
                                  setUserQuery('');
                                  setUserResults([]);
                                }}
                                className="w-full flex items-center gap-2 px-2 py-2 text-left hover:bg-shadow/80 disabled:opacity-40"
                              >
                                <UserAvatar user={u} className="w-8 h-8 rounded-full" emojiClassName="text-sm" />
                                <span className="text-ash text-sm truncate">
                                  {u.displayname || u.username}
                                  {isSelf ? ' (you)' : ''}
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </>
              )}
            </div>
            <div>
              <label htmlFor="postcard-message" className="block text-ash text-sm font-medium mb-1">
                Message on postcard
              </label>
              <textarea
                id="postcard-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 rounded-xl bg-shadow border border-charcoal text-ash text-sm placeholder-silver focus:outline-none focus:ring-2 focus:ring-ether/50 resize-y"
                placeholder="What should we write?"
                data-testid="postcard-message"
              />
            </div>
            <div>
              <label htmlFor="postcard-doodle" className="block text-ash text-sm font-medium mb-1">
                Doodle / art notes
              </label>
              <textarea
                id="postcard-doodle"
                value={doodle}
                onChange={(e) => setDoodle(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 rounded-xl bg-shadow border border-charcoal text-ash text-sm placeholder-silver focus:outline-none focus:ring-2 focus:ring-ether/50 resize-y"
                placeholder="Optional — e.g. small hearts, their initials…"
                data-testid="postcard-doodle"
              />
            </div>
          </div>
        )}

        {needsShipping && (
          <ShippingAddressSection
            selectedAddress={selectedShippingAddress}
            onSelectAddress={setSelectedShippingAddress}
          />
        )}
      </div>

      <div className="flex flex-shrink-0 flex-wrap gap-2 items-center border-t border-shadow bg-charcoal px-4 py-4">
        <label className="text-ash text-sm">Qty:</label>
        <input
          type="number"
          min={1}
          value={quantity}
          onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))}
          className="w-16 px-2 py-1 rounded-lg bg-shadow border border-charcoal text-ash text-sm"
        />
        <button
          type="button"
          onClick={handleCheckout}
          disabled={ordering || !item.is_available}
          className="flex-1 py-2 rounded-xl bg-ether text-void font-medium disabled:opacity-50"
        >
          {ordering ? 'Redirecting...' : 'Checkout'}
        </button>
      </div>
    </div>
  );
}

export default StoreItemDetail;
